import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, ClickAwayListener, Popper, Snackbar, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addVendorsToRfq } from "actions/rfqActions";
import ConfirmDialog from "components/ConfirmDialog";
import authService from "services/authService";
import businessService from "services/businessService";
import rfqService from "services/rfqService";
import { FilterSection, TableSection } from "views/CompanyDirectory/components";
import FilterPanel from "views/NeevayNetwork/components/FilterPanel";
import BusinessNameSearchBar, { mapProfileToDrawerData } from "views/NeevayNetwork/components/BusinessNameSearchBar";
import SearchCategoryTable from "views/NeevayNetwork/components/SearchCategoryTable";
import SearchNew from "views/NeevayNetwork/components/SearchNew";
import Table from "views/NeevayNetwork/components/Table";
import BusinessInfoDrawer from "views/NeevayNetwork/components/tableComponenets/BusinessInfoDrawer";
import VendorSelectionCountSummary from "./VendorSelectionCountSummary";

const inviteTabs = [
  { key: "recommended", label: "Recommended" },
  { key: "internal", label: "Internal", hint: "Add more" },
  { key: "marketplace", label: "Neevay Marketplace", hint: "Add more" },
  { key: "selected", label: "Selected" },
];

const marketplaceVendorTypeOptions = [
  {
    key: "all",
    label: "Both",
    searchValue: "",
    facetValues: [],
    filterStr: "",
  },
  {
    key: "contractor",
    label: "Contractor",
    searchValue: "Contractor (Providing and Fixing)",
    facetValues: ["Contractor (Providing and Fixing)", "Labour Contractor"],
    filterStr: 'businessProfile.businessCategory_str:("Contractor (Providing and Fixing)" OR "Labour Contractor")',
  },
  {
    key: "material",
    label: "Material",
    searchValue: "Material Supplier",
    facetValues: ["Material Supplier"],
    filterStr: 'businessProfile.businessCategory_str:("Material Supplier")',
  },
];
const AiSparkle = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2.5l1.6 5.1c.2.7.8 1.2 1.5 1.5l5.1 1.6-5.1 1.6c-.7.2-1.2.8-1.5 1.5L12 19l-1.6-5.1c-.2-.7-.8-1.2-1.5-1.5L3.8 10.8l5.1-1.6c.7-.2 1.2-.8 1.5-1.5L12 2.5z"
      fill="currentColor"
    />
    <path
      d="M19 15.5l.6 1.8c.1.3.3.5.6.6l1.8.6-1.8.6c-.3.1-.5.3-.6.6L19 21.5l-.6-1.8c-.1-.3-.3-.5-.6-.6l-1.8-.6 1.8-.6c.3-.1.5-.3.6-.6l.6-1.8z"
      fill="currentColor"
      opacity="0.75"
    />
  </svg>
);

const VendorStoreIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4.5 10.5h15v8.2c0 .7-.6 1.3-1.3 1.3H5.8c-.7 0-1.3-.6-1.3-1.3v-8.2z"
      fill="currentColor"
      opacity="0.55"
    />
    <path
      d="M5.2 4h13.6c.5 0 .9.3 1.1.8l1.1 3.4c.4 1.2-.5 2.3-1.7 2.3-.9 0-1.6-.6-1.8-1.4-.2.8-.9 1.4-1.8 1.4s-1.6-.6-1.8-1.4c-.2.8-.9 1.4-1.8 1.4s-1.6-.6-1.8-1.4c-.2.8-.9 1.4-1.8 1.4s-1.6-.6-1.8-1.4c-.2.8-.9 1.4-1.8 1.4-1.2 0-2.1-1.2-1.7-2.3l1.1-3.4c.1-.5.6-.8 1-.8z"
      fill="currentColor"
      opacity="0.95"
    />
    <path
      d="M9 20v-5.2c0-.4.3-.8.8-.8h4.4c.4 0 .8.3.8.8V20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.95"
    />
  </svg>
);

const ContactCell = ({ contacts = [], field }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const contactList = Array.isArray(contacts) ? contacts : contacts ? [contacts] : [];
  const validContacts = contactList.filter((contact) => contact?.[field]);
  const primaryValue = validContacts[0]?.[field] || "";
  const extraCount = Math.max(validContacts.length - 1, 0);
  const open = Boolean(anchorEl);

  if (!primaryValue) return <span>-</span>;

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <span className="relative inline-flex items-center gap-1.5">
        <span>{primaryValue}</span>
        {extraCount > 0 && (
          <button
            type="button"
            onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
            className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 ring-1 ring-blue-100 hover:bg-blue-100"
          >
            +{extraCount} more
          </button>
        )}
        <Popper open={open} anchorEl={anchorEl} placement="bottom-start" className="z-[90]">
          <div className="mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
              {field === "mobile" ? "Mobile numbers" : "Email addresses"}
            </p>
            <div className="max-h-52 space-y-2 overflow-auto">
              {validContacts.map((contact, index) => (
                <div key={`${field}-${contact[field]}-${index}`} className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="truncate text-xs text-gray-800">{contact.name || `Contact ${index + 1}`}</p>
                  <p className={`truncate text-xs ${field === "mobile" ? "text-emerald-700" : "text-blue-700"}`}>
                    {contact[field]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Popper>
      </span>
    </ClickAwayListener>
  );
};

const getVendorKey = (vendor) =>
  `${vendor?.source || "UNKNOWN"}-${vendor?.vendorId || vendor?.supplierId || vendor?.businessName}`;

const getId = (value) => value?.toString?.() || "";

const normalizeVendorSource = (source = "") => source.toString().trim().toUpperCase();

const normalizeVendorName = (name = "") => name.toString().trim().toLowerCase();

const getVendorIdCandidates = (vendor = {}) =>
  [vendor?.supplierId, vendor?.vendorId, vendor?.id, vendor?._id]
    .map(getId)
    .filter(Boolean);

const getInvitedVendorIdCandidates = (vendor = {}) =>
  [vendor?.supplierId, vendor?.vendorId, vendor?.id, vendor?._id]
    .map(getId)
    .filter(Boolean);

const getInvitedVendorSource = (vendor = {}) =>
  normalizeVendorSource(vendor?.vendorSource || vendor?.source);

const getRecommendationVendorSource = (vendor = {}) =>
  normalizeVendorSource(vendor?.source);
const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "V";

const normalizeContact = (contact = {}) => ({
  name: contact?.name || contact?.Name || "User",
  mobile: contact?.mobile || contact?.businessMobile || contact?.businessMobileNumber || "",
  email: contact?.email || contact?.businessEmail || contact?.businessEmailNumber || "",
});

const uniqueContacts = (contacts = []) => {
  const seen = new Set();

  return contacts
    .map(normalizeContact)
    .filter((contact) => {
      if (!contact.email && !contact.mobile) return false;
      const key = contact.email || contact.mobile;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const normalizeSelectedSubCategories = (category = {}) => {
  const nestedSubCategories = Array.isArray(category?.categories)
    ? category.categories.flatMap((selectedCategory) => selectedCategory?.subCategories || [])
    : [];
  const subCategories = Array.isArray(category?.subCategories) ? category.subCategories : nestedSubCategories;
  const normalized = subCategories
    .map((subCategory) => ({
      subCategoryId: getId(subCategory?.subCategoryId || subCategory?._id),
      subCategoryName: subCategory?.subCategoryName || subCategory?.name || "",
    }))
    .filter((subCategory) => subCategory.subCategoryId || subCategory.subCategoryName);

  if (normalized.length > 0) return normalized;

  if (category?.subCategoryId || category?.subCategoryName) {
    return [{
      subCategoryId: getId(category.subCategoryId),
      subCategoryName: category.subCategoryName || "",
    }];
  }

  return [];
};

const normalizeSelectedCategories = (category = {}) => {
  const categories = Array.isArray(category?.categories) ? category.categories : [];
  const normalized = categories
    .map((selectedCategory) => ({
      categoryId: getId(selectedCategory?.categoryId || selectedCategory?._id),
      categoryName: selectedCategory?.categoryName || selectedCategory?.name || "",
      subCategories: normalizeSelectedSubCategories(selectedCategory),
    }))
    .filter((selectedCategory) => selectedCategory.categoryId || selectedCategory.categoryName);

  if (normalized.length > 0) return normalized;

  if (category?.categoryId || category?.categoryName) {
    return [{
      categoryId: getId(category.categoryId),
      categoryName: category.categoryName || "",
      subCategories: normalizeSelectedSubCategories(category),
    }];
  }

  return [];
};

const validateEmail = (email = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatLocation = (location = {}) =>
  [location?.city, location?.state].filter(Boolean).join(", ");

const normalizeInternalVendor = (vendor = {}) => ({
  ...vendor,
  source: "INTERNAL",
  vendorId: vendor.vendorId || vendor.id,
  supplierId: vendor.supplierId || vendor.neevaySupplierId || null,
  businessName: vendor.businessName || vendor.Company || vendor.vendorName || "Unnamed vendor",
  isNeevayVendor: Boolean(vendor.isNeevayVendor || vendor.onNeevay),
  isPremium: Boolean(vendor.isPremium),
  contactDetails: Array.isArray(vendor.contactDetails) ? vendor.contactDetails : [],
  gstNumber: vendor.gstNumber || "",
  vendorCategories: vendor.vendorCategories || [],
  location: Array.isArray(vendor.location) ? vendor.location[0] || {} : vendor.location || {},
});

const normalizeMarketplaceVendor = (vendor = {}) => ({
  ...vendor,
  source: "MARKETPLACE",
  supplierId: vendor.supplierId,
  businessName: Array.isArray(vendor.businessName)
    ? vendor.businessName.join(", ")
    : vendor.businessName || vendor.Company || "Unnamed vendor",
  businessEmail: vendor.businessEmail || vendor?.contactDetails?.[0]?.email || "",
  businessMobile: vendor.businessMobile || vendor.businessMobileNumber || vendor?.contactDetails?.[0]?.mobile || "",
  owners: Array.isArray(vendor.owners) ? vendor.owners : [],
  isPremium: Boolean(vendor.isPremium || vendor.subscriptionType === "PREMIUM"),
  subscriptionType: vendor.subscriptionType || "",
  vendorCategories: vendor.vendorCategories || [],
  location: vendor.location || {},
});

const buildMarketplaceDrawerData = (vendor = {}, payload = {}) => {
  const profile = payload?.profile || {};
  const mappedProfile = payload?.profile ? mapProfileToDrawerData(profile, payload) : {};
  const businessName = mappedProfile.businessName || vendor.businessName;

  return {
    ...vendor,
    ...mappedProfile,
    supplierId: mappedProfile.supplierId || vendor.supplierId,
    businessName: Array.isArray(businessName)
      ? businessName
      : [businessName].filter(Boolean),
    owners: mappedProfile.owners?.length ? mappedProfile.owners : vendor.owners || [],
    vendorCategories: mappedProfile.vendorCategories?.length
      ? mappedProfile.vendorCategories
      : vendor.vendorCategories || [],
    projects: mappedProfile.projects?.length ? mappedProfile.projects : vendor.projects || [],
  };
};

const fetchMarketplaceOwnerContacts = async (vendor = {}) => {
  if (vendor?.source !== "MARKETPLACE" || !Array.isArray(vendor?.owners) || vendor.owners.length === 0) {
    return [];
  }

  const contactResponses = await Promise.all(
    vendor.owners.map((userId) =>
      rfqService.getUserContacts({ userId }).catch(() => null)
    )
  );

  return contactResponses
    .map((response) => response?.user)
    .filter(Boolean)
    .map((owner) => ({
      name: owner?.name || "User",
      mobile: owner?.mobile || "",
      email: owner?.email || "",
    }));
};
const buildSupplierPayload = (vendor, user, ownerContacts = []) => {
  const contacts = uniqueContacts([
    ...(vendor?.contactDetails || []),
    ...(ownerContacts || []),
    {
      name: "Primary",
      mobile: vendor?.businessMobile,
      email: vendor?.businessEmail,
    },
  ]);
  const isMarketplace = vendor?.source === "MARKETPLACE";
  const supplierId = isMarketplace
    ? vendor?.supplierId?.toString()
    : vendor?.isNeevayVendor && vendor?.supplierId
      ? vendor.supplierId?.toString()
      : vendor?.vendorId?.toString();

  return {
    supplierId,
    businessName: vendor?.businessName || "Unnamed vendor",
    isNeevayVendor: isMarketplace || Boolean(vendor?.isNeevayVendor),
    isPremium: Boolean(vendor?.isPremium),
    contactDetails: contacts.map((contact) => ({ ...contact, name: contact.name || "User" })),
    addedBy: { name: user?.name, userId: user?.userId },
    vendorSource: isMarketplace ? "MARKETPLACE" : "INTERNAL",
  };
};

const buildSelectedMapFromRows = (rows = [], source) => {
  const normalizedRows = source === "MARKETPLACE"
    ? rows.map(normalizeMarketplaceVendor)
    : rows.map(normalizeInternalVendor);

  return normalizedRows.reduce((acc, vendor) => {
    const key = getVendorKey(vendor);
    if (key) acc[key] = vendor;
    return acc;
  }, {});
};

// eslint-disable-next-line no-unused-vars
const VendorCard = ({ taxonomyLookup, vendor, selected, onToggle }) => {
  const contacts = uniqueContacts([
    ...(vendor?.contactDetails || []),
    {
      name: "Primary",
      mobile: vendor?.businessMobile,
      email: vendor?.businessEmail,
    },
  ]).slice(0, 2);
  const categories = [];
  const sourceLabel = vendor?.source === "MARKETPLACE" ? "Marketplace" : "Internal";

  return (
    <button
      type="button"
      onClick={() => onToggle(vendor)}
      className={`w-full rounded-2xl border bg-white text-left shadow-sm transition hover:border-orange-300 ${
        selected ? "border-orange-400 ring-2 ring-orange-100" : "border-gray-200"
      }`}
    >
      <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E6F1FB] text-xs font-bold text-[#0C447C]">
          {getInitials(vendor?.businessName)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-gray-950">
            {vendor?.businessName || "Unnamed vendor"}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-blue-700">
              {sourceLabel}
            </span>
            {vendor?.matchScore >= 100 && (
              <span className="rounded-full bg-[#d9f99d] px-2 py-0.5 font-semibold text-green-700">
                {vendor?.source === "MARKETPLACE" && vendor?.isPremium ? "Highly Recommended" : "Vendor Match"}
              </span>
            )}
            {formatLocation(vendor?.location) && <span>{formatLocation(vendor.location)}</span>}
          </span>
        </span>
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
            selected ? "border-orange-500 bg-orange-500 text-white" : "border-gray-300"
          }`}
        >
          {selected ? "✓" : ""}
        </span>
      </div>

      <div className="space-y-3 px-4 py-3">
        {contacts.length > 0 && (
          <div className="rounded-xl bg-gray-50 px-3 py-2">
            {contacts.map((contact, index) => (
              <p key={`${vendor?.businessName}-contact-${index}`} className="text-xs text-gray-700">
                <span className="font-semibold">{contact.name}</span>
                {contact.mobile && <span className="text-green-700"> | {contact.mobile}</span>}
                {contact.email && <span className="text-blue-700"> | {contact.email}</span>}
              </p>
            ))}
          </div>
        )}

        {categories.length > 0 && (
          <div className="space-y-1">
            {categories.map((category, index) => (
              <div key={`${vendor?.businessName}-category-${index}`} className="rounded-xl border border-gray-100 px-3 py-2">
                <p className="text-[11px] font-semibold text-gray-900">
                  {category.discipline} / {category.category}
                </p>
                {category.subCategories.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {category.subCategories.slice(0, 4).map((subCategory) => (
                      <span
                        key={`${vendor?.businessName}-${index}-${subCategory}`}
                        className="rounded-full bg-gray-50 px-2 py-0.5 text-[10px] text-gray-600"
                      >
                        {subCategory}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

const RecommendedVendorTable = ({
  disabled = false,
  isVendorAlreadyInvited = () => false,
  showRecommendationChip = false,
  title,
  vendors,
  selectedVendorMap,
  onOpenMarketplaceVendor,
  onToggle,
  onToggleAll,
}) => {
  const selectableVendors = vendors.filter((vendor) => !isVendorAlreadyInvited(vendor));
  const allSelected = selectableVendors.length > 0 && selectableVendors.every((vendor) => selectedVendorMap[getVendorKey(vendor)]);
  const selectedCount = selectableVendors.filter((vendor) => selectedVendorMap[getVendorKey(vendor)]).length;
  const isMarketplace = title.toLowerCase().includes("marketplace");
  const manualTabLabel = isMarketplace ? "Neevay Marketplace" : "Internal";

  return (
  <div>
    <div className="my-2 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold text-gray-950">{title}</h3>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
          {selectedCount} selected / {vendors.length}
        </span>
      </div>
    </div>
    {!vendors.length ? (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-8 text-center shadow-sm">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 via-white to-orange-50 text-gray-600 ring-1 ring-gray-200">
          <VendorStoreIcon className="h-7 w-7" />
        </span>
        <h4 className="mt-3 text-sm font-bold text-gray-900">
          No {title.toLowerCase()} matches found
        </h4>
        <p className="mx-auto mt-1 max-w-sm text-xs leading-5 text-gray-500">
          We could not find matching vendors from this source for the selected RFQ category.
          Use the {manualTabLabel} tab to search and add vendors manually.
        </p>
      </div>
    ) : (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div className="max-h-[calc(100vh-260px)] overflow-auto">
      <table className="w-full min-w-[560px] border-collapse">
        <thead>
          <tr className="sticky top-0 z-10 border-b border-gray-200 bg-[#f5f5f3] text-left text-[11px] font-bold uppercase tracking-wide text-gray-600">
            <th className="w-10 px-3 py-2">
              <input
                type="checkbox"
                checked={allSelected}
                disabled={disabled || !selectableVendors.length}
                onChange={() => onToggleAll(selectableVendors, allSelected)}
                className="h-4 w-4 cursor-pointer accent-orange-500 disabled:cursor-not-allowed"
              />
            </th>
            <th className="hidden w-12 px-2 py-2"></th>
            <th className="px-2 py-2">Vendor Name</th>
            <th className="px-3 py-2">Mobile</th>
            <th className="px-3 py-2">Email</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((vendor) => {
            const key = getVendorKey(vendor);
            const selected = Boolean(selectedVendorMap[key]);
            const alreadyInvited = isVendorAlreadyInvited(vendor);
            const rowDisabled = disabled || alreadyInvited;
            const showMatchChip = showRecommendationChip || vendor?.matchScore >= 100;
            const contacts = uniqueContacts([
              ...(vendor?.contactDetails || []),
              {
                name: "Primary",
                mobile: vendor?.businessMobile,
                email: vendor?.businessEmail,
              },
            ]);

            return (
              <tr
                key={key}
                className={`border-b border-gray-200 last:border-b-0 transition ${
                  alreadyInvited ? "bg-gray-50 text-gray-500" : selected ? "hover:bg-gray-50" : "bg-white hover:bg-gray-50"
                }`}
              >
                <td className="w-10 px-3 py-3 align-middle">
                  <input
                    type="checkbox"
                    checked={selected || alreadyInvited}
                    disabled={rowDisabled}
                    onChange={() => !alreadyInvited && onToggle(vendor)}
                    className="h-4 w-4 cursor-pointer accent-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </td>
                <td className="hidden w-12 px-2 py-3 align-middle">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-[11px] font-bold text-blue-800">
                    {getInitials(vendor?.businessName)}
                  </span>
                </td>
                <td className="px-2 py-3 align-middle">
                  <div className="flex flex-col">
                    <span className="grid grid-cols-[minmax(0,1fr)_18px] items-start gap-2 text-[13px] text-gray-950">
                      <span className="min-w-0 break-words">
                        {isMarketplace && onOpenMarketplaceVendor ? (
                          <button
                            type="button"
                            onClick={() => onOpenMarketplaceVendor(vendor)}
                            className="text-left font-medium text-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                            aria-label={`View ${vendor?.businessName || "vendor"} business profile`}
                          >
                            {vendor?.businessName || "Unnamed vendor"}
                          </button>
                        ) : (
                          vendor?.businessName || "Unnamed vendor"
                        )}
                        {alreadyInvited && (
                          <span className="ml-2 inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold text-gray-600">
                            Already invited
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 flex h-[18px] w-[18px] items-center justify-center">
                        {vendor?.source === "MARKETPLACE" && vendor?.isPremium && (
                          <Tooltip
                            title="Vendor has been verified for their services & contact details"
                            placement="top"
                            arrow
                          >
                            <img
                              src="https://storagereponeevaydevcdn.blob.core.windows.net/business/Verified_image_nn.png"
                              alt="verified"
                              className="h-[16px] w-[16px]"
                            />
                          </Tooltip>
                        )}
                      </span>
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500">
                      {showMatchChip && (
                        <span className="rounded-full bg-[#d9f99d] px-2 py-0.5 font-semibold text-green-700">
                          {vendor?.source === "MARKETPLACE" && vendor?.isPremium ? "Highly Recommended" : "Vendor Match"}
                        </span>
                      )}
                      {formatLocation(vendor?.location) && <span>{formatLocation(vendor.location)}</span>}
                    </span>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-emerald-700">
                  <ContactCell contacts={contacts} field="mobile" />
                </td>
                <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-gray-900">
                  <ContactCell contacts={contacts} field="email" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
    )}
  </div>
  );
};

const RfqVendorRecommendations = ({
  category,
  entityId,
  invitedVendors = [],
  maxLimit = 15,
  mode = "create",
  onClose,
  onInviteSuccess,
  open,
  projectId,
  rfqId,
  rfqName,
  rfqType,
  projectLocation,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const rfqData = useSelector((state) => state.rfq);
  const searchResult = useSelector((state) => state.search);
  const user = auth?.user;
  const resolvedEntityId = entityId || user?.userEntity?.entityId;
  const hasRecommendationCategory = Boolean(category?.categoryId || category?.categories?.length);
  const showRecommendedTab = mode !== "addMore" || hasRecommendationCategory;
  const availableTabs = useMemo(
    () =>
      inviteTabs.filter(
        (tab) => showRecommendedTab || ["internal", "marketplace", "selected"].includes(tab.key)
      ),
    [showRecommendedTab]
  );
  const [activeTab, setActiveTab] = useState(showRecommendedTab ? "recommended" : "internal");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recommendations, setRecommendations] = useState({
    internalVendors: [],
    marketplaceVendors: [],
  });
  const [selectedVendorMap, setSelectedVendorMap] = useState({});
  const marketplaceDefaultsAppliedRef = useRef(false);
  const marketplaceDrawerRequestRef = useRef(0);
  const marketplaceBusinessInfoCacheRef = useRef(new Map());
  const [marketplaceBusinessInfo, setMarketplaceBusinessInfo] = useState({});
  const [openMarketplaceBusinessInfoDrawer, setOpenMarketplaceBusinessInfoDrawer] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showMarketplaceCategoryDrawer, setShowMarketplaceCategoryDrawer] = useState(false);
  const [selectedMarketplaceVendorTypeKey, setSelectedMarketplaceVendorTypeKey] = useState("all");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const selectedMarketplaceVendorType = useMemo(
    () => marketplaceVendorTypeOptions.find((option) => option.key === selectedMarketplaceVendorTypeKey) || marketplaceVendorTypeOptions[0],
    [selectedMarketplaceVendorTypeKey]
  );

  const internalRecommendations = useMemo(
    () => (recommendations.internalVendors || []).map(normalizeInternalVendor),
    [recommendations.internalVendors]
  );
  const marketplaceRecommendations = useMemo(
    () => (recommendations.marketplaceVendors || []).map(normalizeMarketplaceVendor),
    [recommendations.marketplaceVendors]
  );
  const allRecommended = useMemo(
    () => [...internalRecommendations, ...marketplaceRecommendations],
    [internalRecommendations, marketplaceRecommendations]
  );
  const selectedVendors = useMemo(() => Object.values(selectedVendorMap), [selectedVendorMap]);
  const selectedInternal = useMemo(
    () => selectedVendors.filter((vendor) => vendor.source === "INTERNAL"),
    [selectedVendors]
  );
  const selectedMarketplace = useMemo(
    () => selectedVendors.filter((vendor) => vendor.source === "MARKETPLACE"),
    [selectedVendors]
  );

  const hasMarketplaceResults = searchResult?.results?.length > 0;
  const invitedCount = invitedVendors?.length || 0;
  const remainingSlots = Math.max(maxLimit - invitedCount - selectedVendors.length, 0);

  const handleCloseMarketplaceBusinessInfo = useCallback(() => {
    marketplaceDrawerRequestRef.current += 1;
    setOpenMarketplaceBusinessInfoDrawer(false);
    setMarketplaceBusinessInfo({});
  }, []);

  const handleOpenMarketplaceBusinessInfo = useCallback(async (vendor) => {
    const requestId = marketplaceDrawerRequestRef.current + 1;
    const supplierId = getId(vendor?.supplierId);
    const cachedBusinessInfo = supplierId
      ? marketplaceBusinessInfoCacheRef.current.get(supplierId)
      : null;
    marketplaceDrawerRequestRef.current = requestId;
    setMarketplaceBusinessInfo(cachedBusinessInfo || buildMarketplaceDrawerData(vendor));
    setOpenMarketplaceBusinessInfoDrawer(true);

    if (!supplierId || cachedBusinessInfo) return;

    try {
      const payload = await businessService.fetchBusinessById(supplierId);
      if (marketplaceDrawerRequestRef.current !== requestId) return;
      const businessInfo = buildMarketplaceDrawerData(vendor, payload);
      marketplaceBusinessInfoCacheRef.current.set(supplierId, businessInfo);
      setMarketplaceBusinessInfo(businessInfo);
    } catch (error) {
      console.error("Failed to fetch marketplace vendor details", error);
    }
  }, []);

  useEffect(() => {
    if (open) return;
    marketplaceDrawerRequestRef.current += 1;
    setOpenMarketplaceBusinessInfoDrawer(false);
    setMarketplaceBusinessInfo({});
  }, [open]);

  const isVendorAlreadyInvited = useCallback((vendor = {}) => {
    const vendorIds = getVendorIdCandidates(vendor);
    const vendorSource = getRecommendationVendorSource(vendor);
    const vendorName = normalizeVendorName(vendor?.businessName);

    return (invitedVendors || []).some((invitedVendor) => {
      const invitedIds = getInvitedVendorIdCandidates(invitedVendor);
      const invitedSource = getInvitedVendorSource(invitedVendor);
      const hasSameSource = !invitedSource || !vendorSource || invitedSource === vendorSource;
      const hasMatchingId = vendorIds.some((vendorId) => invitedIds.includes(vendorId));

      if (hasSameSource && hasMatchingId) return true;

      return hasSameSource && vendorName && vendorName === normalizeVendorName(invitedVendor?.businessName);
    });
  }, [invitedVendors]);

  useEffect(() => {
    if (!open || !showRecommendedTab || !marketplaceRecommendations.length) return;
    if (marketplaceDefaultsAppliedRef.current) return;

    marketplaceDefaultsAppliedRef.current = true;
    const availableSlots = Math.max(maxLimit - invitedCount, 0);
    const defaultVendors = marketplaceRecommendations
      .filter((vendor) => !isVendorAlreadyInvited(vendor))
      .slice(0, Math.min(4, availableSlots));

    setSelectedVendorMap((currentMap) => {
      const nextMap = { ...currentMap };
      defaultVendors.forEach((vendor) => {
        nextMap[getVendorKey(vendor)] = vendor;
      });
      return nextMap;
    });
  }, [invitedCount, isVendorAlreadyInvited, marketplaceRecommendations, maxLimit, open, showRecommendedTab]);

  const fallbackSubCategoryName = rfqName && rfqName !== category?.categoryName ? rfqName : "";
  const selectedSubCategories = useMemo(
    () => normalizeSelectedSubCategories(category),
    [category]
  );
  const selectedCategories = useMemo(
    () => normalizeSelectedCategories(category),
    [category]
  );
  const selectedSubCategoryNames = useMemo(
    () => selectedSubCategories.map((subCategory) => subCategory.subCategoryName).filter(Boolean),
    [selectedSubCategories]
  );
  const selectedCategoryNames = useMemo(
    () => selectedCategories.map((selectedCategory) => selectedCategory.categoryName).filter(Boolean),
    [selectedCategories]
  );
  const displaySubCategoryName = selectedSubCategoryNames.length
    ? selectedSubCategoryNames.join(", ")
    : selectedCategoryNames.length
      ? selectedCategoryNames.join(", ")
      : fallbackSubCategoryName;
  const recommendationCategory = useMemo(() => ({
    ...(category || {}),
    categoryId: selectedCategories[0]?.categoryId || category?.categoryId || "",
    categoryName: selectedCategories[0]?.categoryName || category?.categoryName || "",
    categoryIds: selectedCategories.map((selectedCategory) => selectedCategory.categoryId).filter(Boolean),
    categories: selectedCategories,
    subCategoryId: selectedSubCategories[0]?.subCategoryId || "",
    subCategoryName: selectedSubCategories[0]?.subCategoryName || "",
    subCategories: selectedSubCategories,
    subCategoryIds: selectedSubCategories.map((subCategory) => subCategory.subCategoryId).filter(Boolean),
    searchNames: selectedSubCategoryNames.length ? selectedSubCategoryNames : selectedCategoryNames,
    searchName: selectedSubCategoryNames.join(" ") || selectedCategoryNames.join(" ") || fallbackSubCategoryName || category?.categoryName || "",
  }), [category, fallbackSubCategoryName, selectedCategories, selectedCategoryNames, selectedSubCategories, selectedSubCategoryNames]);

  useEffect(() => {
    if (!open) return;

    if (!showRecommendedTab) {
      setActiveTab("internal");
      setSelectedVendorMap({});
      dispatch({ type: "SEARCH_RESET" });
      return;
    }

    if (!rfqId || !hasRecommendationCategory || !resolvedEntityId) return;

    let isMounted = true;

    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        setActiveTab("recommended");
        marketplaceDefaultsAppliedRef.current = false;
        setSelectedVendorMap({});
        dispatch({ type: "SEARCH_RESET" });

        const response = await authService.getRfqVendorRecommendations({
          entityId: resolvedEntityId,
          category: recommendationCategory,
          projectId,
          projectLocation,
          rfqType,
          limit: 50,
        });

        if (!isMounted) return;
        const recommendationPayload = response?.payload || { internalVendors: [], marketplaceVendors: [] };

        setRecommendations(recommendationPayload);
        setSelectedVendorMap({});
      } catch (error) {
        if (!isMounted) return;
        setRecommendations({ internalVendors: [], marketplaceVendors: [] });
        setSnackbar({
          open: true,
          severity: "error",
          message: "Could not load vendor recommendations. You can still use the internal or marketplace tabs.",
        });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRecommendations();

    return () => {
      isMounted = false;
    };
  }, [dispatch, hasRecommendationCategory, open, projectId, projectLocation, recommendationCategory, resolvedEntityId, rfqId, rfqType, showRecommendedTab]);

  const toggleRecommendedVendor = (vendor) => {
    if (submitting || isVendorAlreadyInvited(vendor)) return;

    setSelectedVendorMap((currentMap) => {
      const key = getVendorKey(vendor);
      const nextMap = { ...currentMap };

      if (nextMap[key]) {
        delete nextMap[key];
        return nextMap;
      }

      if (Object.keys(nextMap).length + invitedCount >= maxLimit) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: `You can add only ${maxLimit} vendors to this RFQ.`,
        });
        return currentMap;
      }

      nextMap[key] = vendor;
      return nextMap;
    });
  };

  const toggleRecommendedVendorGroup = (vendors = [], allSelected = false) => {
    if (submitting) return;

    const selectableVendors = vendors.filter((vendor) => !isVendorAlreadyInvited(vendor));

    setSelectedVendorMap((currentMap) => {
      const nextMap = { ...currentMap };

      if (allSelected) {
        selectableVendors.forEach((vendor) => {
          delete nextMap[getVendorKey(vendor)];
        });
        return nextMap;
      }

      const availableSlots = maxLimit - invitedCount - Object.keys(nextMap).length;
      const unselectedVendors = selectableVendors.filter((vendor) => !nextMap[getVendorKey(vendor)]);
      const vendorsToAdd = unselectedVendors.slice(0, Math.max(availableSlots, 0));

      if (vendorsToAdd.length < unselectedVendors.length) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: `You can add only ${maxLimit} vendors to this RFQ.`,
        });
      }

      vendorsToAdd.forEach((vendor) => {
        nextMap[getVendorKey(vendor)] = vendor;
      });

      return nextMap;
    });
  };
  const updateSourceSelection = useCallback((source, rows = []) => {
    if (submitting) return;

    setSelectedVendorMap((currentMap) => {
      const nextMap = Object.entries(currentMap).reduce((acc, [key, vendor]) => {
        if (vendor.source !== source) acc[key] = vendor;
        return acc;
      }, {});
      const sourceMap = buildSelectedMapFromRows(rows, source);
      const availableSlots = maxLimit - invitedCount - Object.keys(nextMap).length;
      const sourceEntries = Object.entries(sourceMap);

      if (sourceEntries.length > availableSlots) {
        setSnackbar({
          open: true,
          severity: "warning",
          message: `You can add only ${maxLimit} vendors to this RFQ.`,
        });
      }

      return {
        ...nextMap,
        ...Object.fromEntries(sourceEntries.slice(0, Math.max(availableSlots, 0))),
      };
    });
  }, [invitedCount, maxLimit, submitting]);

  const handleClearSelection = () => {
    if (submitting) return;

    setSelectedVendorMap({});
  };

  const handleRequestClose = () => {
    if (mode === "addMore" || !showRecommendedTab) {
      onClose?.();
      return;
    }

    setShowCloseConfirmation(true);
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    onClose?.();
    navigate(`/rfqs/${rfqId}/Overview`);
  };

  const handleInviteSelected = async () => {
    if (!selectedVendors.length) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Select at least one vendor before sending invitations.",
      });
      return;
    }

    if (selectedVendors.length + invitedCount > maxLimit) {
      setSnackbar({
        open: true,
        severity: "error",
        message: `You can add only ${maxLimit} vendors to this RFQ.`,
      });
      return;
    }

    const suppliers = await Promise.all(
      selectedVendors.map(async (vendor) => {
        const ownerContacts = await fetchMarketplaceOwnerContacts(vendor);
        return buildSupplierPayload(vendor, user, ownerContacts);
      })
    );
    const missingOrInvalidEmail = suppliers.some(
      (supplier) => !(supplier.contactDetails || []).some((contact) => validateEmail(contact.email))
    );

    if (missingOrInvalidEmail) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Some selected vendors do not have a valid email. Please review vendor contacts.",
      });
      return;
    }

    try {
      setSubmitting(true);
      await dispatch(
        addVendorsToRfq({
          rfqId,
          suppliers,
          requesterName: user?.name,
          requestedBy: user?.email,
          senderEmail: user?.email,
        })
      );
      onInviteSuccess?.(selectedVendors.length);
      onClose?.();
      navigate(`/rfqs/${rfqId}/Vendors`);
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Failed to invite selected vendors.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const tabCounts = {
    recommended: allRecommended.length,
    internal: selectedInternal.length,
    marketplace: selectedMarketplace.length,
    selected: selectedVendors.length,
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40">
      <div
        className="relative flex h-full flex-col bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF] shadow-2xl"
        style={{ width: "calc(100% - 80px)" }}
      >
        <div className="border-b border-gray-200 bg-white px-7 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-700 shadow-sm">
                <VendorStoreIcon />
              </span>
              <h2 className="text-lg font-bold tracking-tight text-gray-950">
                Invite vendors
              </h2>
              {showRecommendedTab && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-50 to-orange-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-100">
                  <AiSparkle className="h-4 w-4" />
                  AI Recommended
                </span>
              )}
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                RFQ #{rfqId}
              </span>
              <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium capitalize text-orange-700">
                {rfqType}
              </span>
              <span className="max-w-[360px] truncate rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                {[category?.disciplineName, category?.categoryName, displaySubCategoryName]
                  .filter(Boolean)
                  .join(" / ")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={submitting}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleRequestClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-20">
          <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
            <div className="flex">
              {availableTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  disabled={submitting}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition ${
                    activeTab === tab.key
                      ? "border-orange-500 text-black"
                      : "border-transparent text-gray-500 hover:text-gray-800"
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {tab.key === "recommended" && <AiSparkle className="h-3.5 w-3.5 text-blue-600" />}
                  {tab.label}
                  <span className="text-xs">({tabCounts[tab.key] || 0})</span>
                  {tab.hint && activeTab !== tab.key && (
                    <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold text-orange-700 ring-1 ring-orange-100">
                      {tab.hint}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="pb-1 text-xs font-medium text-gray-500">
              Select across tabs. Final send happens from the bottom bar.
            </div>
          </div>

          {activeTab === "recommended" && (
            loading ? (
              <div className="flex h-72 flex-col items-center justify-center rounded-2xl border border-dashed border-orange-200">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-orange-100 border-t-orange-500" />
                <p className="mt-4 text-sm font-medium text-gray-700">Finding best matching vendors...</p>
              </div>
            ) : allRecommended.length ? (
              <div>
                <div className="grid gap-5 xl:grid-cols-2">
                  <RecommendedVendorTable
                    disabled={submitting}
                    title="Internal"
                    vendors={internalRecommendations}
                    selectedVendorMap={selectedVendorMap}
                    isVendorAlreadyInvited={isVendorAlreadyInvited}
                    showRecommendationChip
                    onToggle={toggleRecommendedVendor}
                    onToggleAll={toggleRecommendedVendorGroup}
                  />
                  <RecommendedVendorTable
                    disabled={submitting}
                    title="Neevay Marketplace"
                    vendors={marketplaceRecommendations}
                    selectedVendorMap={selectedVendorMap}
                    isVendorAlreadyInvited={isVendorAlreadyInvited}
                    onOpenMarketplaceVendor={handleOpenMarketplaceBusinessInfo}
                    showRecommendationChip
                    onToggle={toggleRecommendedVendor}
                    onToggleAll={toggleRecommendedVendorGroup}
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600">
                No recommendation found. Use Internal or Neevay Marketplace tab to search manually.
              </div>
            )
          )}

          {activeTab === "internal" && (
            <div className="flex min-h-0 flex-1 gap-2 overflow-hidden rounded-2xl bg-white">
              <div className="flex shrink-0 flex-col overflow-hidden rounded-xl">
                <div className="px-3 py-2">
                  <h3 className="text-sm font-bold text-gray-900">Internal Vendors</h3>
                  <p className="mt-0.5 text-[11px] text-gray-500">Filter and select from your entity directory</p>
                </div>
                <FilterSection />
              </div>
              <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-100 bg-white">
                <TableSection
                  isSliderView={true}
                  selectionOnly={true}
                  rfqId={rfqId}
                  invitedVendors={rfqData?.rfq?.suppliers || invitedVendors}
                  maxLimit={maxLimit}
                  preselectedVendors={selectedInternal}
                  hideCategoryQuickFilter={true}
                  onSelectionChange={(rows) => updateSourceSelection("INTERNAL", rows)}
                />
              </div>
            </div>
          )}

          {activeTab === "marketplace" && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="grid gap-3 border-b border-gray-100 px-5 py-3 xl:grid-cols-[minmax(220px,1fr)_minmax(250px,300px)_minmax(360px,2fr)_auto] xl:items-end">
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-bold text-gray-900">Business name</p>
                  <BusinessNameSearchBar
                    vendorType={selectedMarketplaceVendorType?.key || ""}
                    selectionMode
                    selectedVendors={selectedMarketplace}
                    onSelectionChange={(rows) => updateSourceSelection("MARKETPLACE", rows)}
                  />
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-bold text-gray-900">Vendor type</p>
                  <div className="grid h-[42px] grid-cols-3 overflow-hidden rounded-md border border-[#F1D7C8] bg-white/95 shadow-sm">
                    {marketplaceVendorTypeOptions.map((option, index) => {
                      const isSelected = selectedMarketplaceVendorType?.key === option.key;

                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setSelectedMarketplaceVendorTypeKey(option.key)}
                          className={`inline-flex min-w-0 items-center justify-center px-2 text-xs font-medium transition ${
                            isSelected
                              ? "bg-[linear-gradient(135deg,#FFF1E8_0%,#FFF9F3_100%)] text-[#B43A12] shadow-inner"
                              : "bg-white text-[#374151] hover:bg-[#FFF8F3]"
                          } ${index > 0 ? "border-l border-[#F1D7C8]" : ""}`}
                        >
                          <span className="truncate">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-bold text-gray-900">Search by material / service and region</p>
                  <SearchNew
                    compact
                    externalVendorType={selectedMarketplaceVendorType?.searchValue || ""}
                    externalFilterStr={selectedMarketplaceVendorType?.filterStr || ""}
                    externalFacetValue={selectedMarketplaceVendorType?.searchValue || ""}
                    externalFacetValues={selectedMarketplaceVendorType?.facetValues || []}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowMarketplaceCategoryDrawer(true)}
                  className="inline-flex h-[42px] shrink-0 items-center justify-center gap-2 rounded-md border border-[#F7B99D] bg-[#FFF4EE] px-3 text-xs font-medium text-[#B43A12] transition-colors hover:border-[#E95420]"
                >
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[11px] font-bold text-[#E95420] shadow-sm">+</span>
                  Search by Category
                </button>
              </div>
              {showMarketplaceCategoryDrawer && (
                <div className="fixed inset-0 z-[70] flex justify-end bg-black/25" onClick={() => setShowMarketplaceCategoryDrawer(false)}>
                  <div className="h-full w-[min(1120px,calc(100vw-32px))] overflow-hidden bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                      <div>
                        <p className="text-sm font-bold text-gray-900">Browse by Category</p>
                        <p className="text-xs text-gray-500">Search marketplace vendors by activity and city.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowMarketplaceCategoryDrawer(false)}
                        className="rounded-full px-3 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      >
                        Close
                      </button>
                    </div>
                    <div className="h-[calc(100%-57px)] overflow-y-auto p-4">
                      <SearchCategoryTable
                        isInDrawer={true}
                        onClose={() => setShowMarketplaceCategoryDrawer(false)}
                        sliderName="NN"
                        allowedCategoryType={selectedMarketplaceVendorType?.key === "all" ? "all" : selectedMarketplaceVendorType?.key || "none"}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex min-h-0 flex-1 gap-2 overflow-hidden">
                {hasMarketplaceResults && (
                  <div className="w-[250px] shrink-0 overflow-hidden rounded-xl border border-gray-100">
                    <FilterPanel />
                  </div>
                )}
                <div className="min-w-0 flex-1 overflow-y-auto rounded-xl border border-gray-100 bg-white">
                  {hasMarketplaceResults ? (
                    <Table
                      isSliderView={true}
                      selectionOnly={true}
                      rfqId={rfqId}
                      invitedVendors={rfqData?.rfq?.suppliers || invitedVendors}
                      maxLimit={maxLimit}
                      preselectedVendors={selectedMarketplace}
                      onSelectionChange={(rows) => updateSourceSelection("MARKETPLACE", rows)}
                    />
                  ) : (
                    <SearchCategoryTable
                      isInDrawer={false}
                      sliderName="NN"
                      allowedCategoryType={selectedMarketplaceVendorType?.key === "all" ? "all" : selectedMarketplaceVendorType?.key || "none"}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "selected" && (
            selectedVendors.length ? (
              <div className="grid gap-5 xl:grid-cols-2">
                {selectedInternal.length > 0 && (
                  <RecommendedVendorTable
                    disabled={submitting}
                    title="Internal"
                    vendors={selectedInternal}
                    selectedVendorMap={selectedVendorMap}
                    isVendorAlreadyInvited={isVendorAlreadyInvited}
                    onToggle={toggleRecommendedVendor}
                    onToggleAll={toggleRecommendedVendorGroup}
                  />
                )}
                {selectedMarketplace.length > 0 && (
                  <RecommendedVendorTable
                    disabled={submitting}
                    title="Neevay Marketplace"
                    vendors={selectedMarketplace}
                    selectedVendorMap={selectedVendorMap}
                    isVendorAlreadyInvited={isVendorAlreadyInvited}
                    onOpenMarketplaceVendor={handleOpenMarketplaceBusinessInfo}
                    onToggle={toggleRecommendedVendor}
                    onToggleAll={toggleRecommendedVendorGroup}
                  />
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-600">
                No vendors selected yet. Select vendors from Recommended, Internal, or Neevay Marketplace tabs.
              </div>
            )
          )}
        </div>

        {submitting && (
          <div className="absolute inset-x-0 bottom-[73px] top-[58px] z-[65] flex items-center justify-center bg-white/55 backdrop-blur-[1px]">
            <div className="rounded-2xl border border-orange-100 bg-white px-5 py-4 text-center shadow-xl">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-orange-100 border-t-orange-500" />
              <p className="mt-3 text-sm font-semibold text-gray-900">Sending invitations...</p>
              <p className="mt-1 text-xs text-gray-500">Please wait while we add selected vendors to this RFQ.</p>
            </div>
          </div>
        )}

        <div className="fixed bottom-0 right-0 z-[70] border-t border-gray-200 bg-white px-7 py-3 shadow-[0_-6px_24px_rgba(0,0,0,.08)]" style={{ width: "calc(100% - 80px)" }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <VendorSelectionCountSummary
              selectedCount={selectedVendors.length}
              internalCount={selectedInternal.length}
              marketplaceCount={selectedMarketplace.length}
              remainingSlots={remainingSlots}
              remainingLabel="slot remaining"
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClearSelection}
                disabled={!selectedVendors.length || submitting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={handleInviteSelected}
                disabled={!selectedVendors.length || submitting}
                className="rounded-md bg-[#1E1E08] px-5 py-2 text-sm font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {submitting ? "Sending..." : selectedVendors.length ? `Send to ${selectedVendors.length} vendors` : "Send invitations"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <BusinessInfoDrawer
        handleOpenBusinessInfoDrawerClose={handleCloseMarketplaceBusinessInfo}
        values={marketplaceBusinessInfo}
        openBusinessInfoDrawer={openMarketplaceBusinessInfoDrawer}
      />

      <ConfirmDialog
        isOpen={showCloseConfirmation}
        title="Close vendor invitation?"
        description="This RFQ is already saved as draft. If you close now, you will be redirected to the RFQ overview and can invite vendors later."
        cancelAction={handleCancelClose}
        confirmAction={handleConfirmClose}
      />
    </div>
  );
};

export default RfqVendorRecommendations;


